import { FilterOperator, FilterMatchMode } from 'primereact/api';
import { FilterOperator as FilterOperator$1 } from './types.js';

function isQueryColumn(obj) {
    return typeof obj === "object" && obj !== null && !("catalogId" in obj);
}
function isQueryColumns(arr) {
    return arr.length > 0 && isQueryColumn(arr[0]);
}
function mapMatchModeToFilterOperator(matchMode) {
    switch (matchMode) {
        case FilterMatchMode.DATE_IS:
        case FilterMatchMode.EQUALS: {
            return FilterOperator$1.Equals;
        }
        case FilterMatchMode.DATE_IS_NOT:
        case FilterMatchMode.NOT_EQUALS: {
            return FilterOperator$1.NotEquals;
        }
        case FilterMatchMode.STARTS_WITH: {
            return FilterOperator$1.StartsWithCaseInsensitive;
        }
        case FilterMatchMode.CONTAINS: {
            return FilterOperator$1.ContainsCaseInsensitive;
        }
        case FilterMatchMode.NOT_CONTAINS: {
            return FilterOperator$1.NotContainsCaseInsensitive;
        }
        case FilterMatchMode.ENDS_WITH: {
            return FilterOperator$1.EndsWithCaseInsensitive;
        }
        case FilterMatchMode.DATE_BEFORE:
        case FilterMatchMode.LESS_THAN: {
            return FilterOperator$1.LessThan;
        }
        case FilterMatchMode.LESS_THAN_OR_EQUAL_TO: {
            return FilterOperator$1.LessThanOrEquals;
        }
        case FilterMatchMode.DATE_AFTER:
        case FilterMatchMode.GREATER_THAN: {
            return FilterOperator$1.GreaterThan;
        }
        case FilterMatchMode.GREATER_THAN_OR_EQUAL_TO: {
            return FilterOperator$1.GreaterThanOrEquals;
        }
        case FilterMatchMode.BETWEEN: {
            return FilterOperator$1.Between;
        }
        default: {
            return FilterOperator$1.Empty;
        }
    }
}
function notUndefined(thing) {
    return typeof thing !== "undefined";
}
function excludeFilterValue(value) {
    if (value === null) {
        return true;
    }
    if (typeof value === "string" && value === "") {
        return true;
    }
    return false;
}
function buildQueryFromFilters(filters, columns, tables) {
    const filterOrUndefined = Object.entries(filters).flatMap(([field, fieldFilters]) => {
        if (isQueryColumns(columns)) {
            const column = columns.find((column) => field === column.name);
            if (!column) {
                return [];
            }
            const table = tables[0];
            const selectPartColumn = Object.assign(Object.assign({}, column), { props: null, catalogId: table.catalogId, schemaName: table.schemaName, tableName: table.tableName, columnName: column.name });
            const isSingleFilter = !("operator" in fieldFilters);
            if (isSingleFilter) {
                if (excludeFilterValue(fieldFilters.value)) {
                    return [];
                }
                return [
                    [
                        {
                            column: selectPartColumn,
                            function: null
                        },
                        mapMatchModeToFilterOperator(fieldFilters.matchMode),
                        [fieldFilters.value]
                    ]
                ];
            }
            if (fieldFilters.constraints.length === 1 &&
                excludeFilterValue(fieldFilters.constraints[0].value)) {
                return [];
            }
            return {
                [fieldFilters.operator]: fieldFilters.constraints.flatMap((constraint) => {
                    if (excludeFilterValue(constraint.value)) {
                        return [];
                    }
                    return [
                        [
                            {
                                column: selectPartColumn,
                                function: null
                            },
                            mapMatchModeToFilterOperator(constraint.matchMode),
                            [constraint.value]
                        ]
                    ];
                })
            };
        }
        const column = columns.find((column) => field === column.props.name);
        if (!column) {
            return [];
        }
        const isSingleFilter = !("operator" in fieldFilters);
        if (isSingleFilter) {
            if (excludeFilterValue(fieldFilters.value)) {
                return [];
            }
            return [
                [
                    {
                        column,
                        function: null
                    },
                    mapMatchModeToFilterOperator(fieldFilters.matchMode),
                    [fieldFilters.value]
                ]
            ];
        }
        if (fieldFilters.constraints.length === 1 &&
            excludeFilterValue(fieldFilters.constraints[0].value)) {
            return [];
        }
        return [
            {
                [fieldFilters.operator]: fieldFilters.constraints.flatMap((constraint) => {
                    if (excludeFilterValue(constraint.value)) {
                        return [];
                    }
                    return [
                        [
                            {
                                column,
                                function: null
                            },
                            mapMatchModeToFilterOperator(constraint.matchMode),
                            [constraint.value]
                        ]
                    ];
                })
            }
        ];
    });
    return filterOrUndefined.filter(notUndefined);
}
function buildOrderByField(sortMeta, columns, tables) {
    return sortMeta.flatMap((sortItem) => {
        const { field, order } = sortItem;
        if (!(order === 1 || order === -1)) {
            return [];
        }
        if (isQueryColumns(columns)) {
            const column = columns.find((column) => field === column.name);
            if (!column) {
                return [];
            }
            const table = tables[0];
            const orderByColumn = Object.assign(Object.assign({}, column), { props: null, catalogId: table.catalogId, schemaName: table.schemaName, tableName: table.tableName, columnName: column.name });
            return [{
                    column: orderByColumn,
                    order: order === 1 ? "asc" : "desc"
                }];
        }
        const column = columns.find(column => column.props.name === field);
        if (!column) {
            return [];
        }
        return [{
                column,
                order: order === 1 ? "asc" : "desc"
            }];
    });
}
function mergeVisualSqlWithDataTableFilters(visualSqlQuery, filters, columns, sortMeta) {
    let query = visualSqlQuery;
    const tables = visualSqlQuery.from;
    const baseFilters = visualSqlQuery.filters;
    const baseOrderBy = visualSqlQuery.orderBy;
    if (baseFilters === null) {
        const builtFilters = buildQueryFromFilters(filters, columns, tables);
        query = Object.assign(Object.assign({}, query), { filters: builtFilters.length > 0 ? {
                and: builtFilters
            } : null });
    }
    else {
        query = Object.assign(Object.assign({}, query), { filters: {
                and: [
                    { and: buildQueryFromFilters(filters, columns, tables) },
                    baseFilters
                ]
            } });
    }
    if (baseOrderBy === null) {
        const builtOrderBy = buildOrderByField(sortMeta, columns, tables);
        query = Object.assign(Object.assign({}, query), { orderBy: builtOrderBy.length > 0 ? builtOrderBy : null });
    }
    else {
        query = Object.assign(Object.assign({}, query), { orderBy: [
                ...baseOrderBy,
                ...buildOrderByField(sortMeta, columns, tables),
            ] });
    }
    return query;
}
function getFilterableColumnDataType(columnDataType) {
    if (typeof columnDataType === "undefined") {
        return;
    }
    return Object.keys(dataTypeRegex).find((type) => {
        const regExpList = dataTypeRegex[type];
        return (typeof regExpList.find((regexp) => {
            return regexp.test(columnDataType);
        }) !== "undefined");
    });
}
const dataTypeRegex = {
    text: [
        new RegExp("^char$"),
        new RegExp("^varchar$"),
        new RegExp("^varchar\\(\\d+\\)$"),
        new RegExp("^text$"),
        new RegExp("^varbinary$"),
        new RegExp("^blob$"),
        new RegExp("^json$")
    ],
    numeric: [
        new RegExp("^tinyint$"),
        new RegExp("^smallint$"),
        new RegExp("^integer$"),
        new RegExp("^bigint$"),
        new RegExp("^numeric$"),
        new RegExp("^real$"),
        new RegExp("^double$"),
        new RegExp("^decimal(\\(\\d+(,\\d+)?\\))?$")
    ],
    date: [
        new RegExp("^date$"),
        new RegExp("^time$"),
        new RegExp("^time\\(\\d+\\)$"),
        new RegExp("^time with time zone$"),
        new RegExp("^timestamp$"),
        new RegExp("^timestamp\\(\\d+\\)$"),
        new RegExp("^timestamp with time zone$"),
        new RegExp("^timestamp\\(\\d+\\) with time zone$"),
        new RegExp("^timestamp with time zone\\(\\d+\\)$"),
        new RegExp("^interval year to month$"),
        new RegExp("^interval day to second$")
    ],
    boolean: [new RegExp("^boolean$")]
};
function getDefaultFilters(columns) {
    return Object.fromEntries(
    // @ts-ignore primereact wtf bro
    columns.flatMap((column) => {
        const { dataType, field, filterable } = column;
        if (!dataType || filterable === false) {
            return [];
        }
        switch (dataType) {
            case "text": {
                return [
                    [
                        field,
                        {
                            operator: FilterOperator.AND,
                            constraints: [
                                { value: "", matchMode: FilterMatchMode.CONTAINS }
                            ]
                        }
                    ]
                ];
            }
            case "numeric": {
                return [
                    [
                        field,
                        {
                            operator: FilterOperator.AND,
                            constraints: [
                                { value: null, matchMode: FilterMatchMode.EQUALS }
                            ]
                        }
                    ]
                ];
            }
            case "boolean": {
                return [
                    [
                        field,
                        {
                            operator: FilterOperator.AND,
                            constraints: [
                                { value: null, matchMode: FilterMatchMode.EQUALS }
                            ]
                        }
                    ]
                ];
            }
            case "date": {
                return [
                    [
                        field,
                        {
                            operator: FilterOperator.AND,
                            constraints: [
                                { value: null, matchMode: FilterMatchMode.DATE_IS }
                            ]
                        }
                    ]
                ];
            }
            default: {
                return [];
            }
        }
    }));
}

export { buildOrderByField, buildQueryFromFilters, getDefaultFilters, getFilterableColumnDataType, isQueryColumn, isQueryColumns, mergeVisualSqlWithDataTableFilters };
//# sourceMappingURL=tableUtils.js.map
